import { useId, useState, type FormEvent, type ReactElement } from 'react';
import {
  ANTHROPIC_MODELS,
  AnthropicAppConfigPutBody,
  AnthropicAppConfigValidationError,
  modelLabel,
  useUpdateAnthropicAppConfig,
  type AnthropicAppConfigPutResponse,
  type AnthropicAuthMode,
  type AnthropicModel,
} from '@/lib/anthropic';

/**
 * Formulario para configurar la API key de Anthropic Claude del profile.
 * Renderizado en dos escenarios:
 *
 *  - Modo "create" (estado 1): el usuario aún no ha guardado nada.
 *  - Modo "edit"   (desde estado 2): pre-cargamos `modelDefault` y
 *    `monthlyBudgetEur` pero EXIGIMOS reintroducir la apiKey — el backend
 *    nunca la devuelve por seguridad, y cambiarla resetea el contador de
 *    tokens consumidos del mes en curso. Avisamos arriba de ese efecto.
 *
 * Decisión UX: NO permitimos un PUT parcial sin apiKey aunque sólo cambien
 * el modelo o el budget. El contrato exige `{ apiKey, modelDefault,
 * monthlyBudgetEur }` siempre — más simple y robusto para evitar inconsistencias.
 */
export function AnthropicAppConfigForm({
  initialAuthMode,
  initialModelDefault,
  initialMonthlyBudgetEur,
  mode = 'create',
  onSaved,
  onCancel,
}: {
  initialAuthMode?: AnthropicAuthMode;
  initialModelDefault?: AnthropicModel;
  initialMonthlyBudgetEur?: number;
  mode?: 'create' | 'edit';
  onSaved?: (response: AnthropicAppConfigPutResponse) => void;
  onCancel?: () => void;
}): ReactElement {
  const apiKeyId = useId();
  const modelId = useId();
  const budgetId = useId();
  const instructionsId = useId();
  const formErrorsId = useId();
  const authModeId = useId();

  const [authMode, setAuthMode] = useState<AnthropicAuthMode>(
    initialAuthMode ?? 'api_key',
  );
  const [apiKey, setApiKey] = useState('');
  const [modelDefault, setModelDefault] = useState<AnthropicModel>(
    initialModelDefault ?? 'claude-sonnet-4-6',
  );
  const [monthlyBudgetEur, setMonthlyBudgetEur] = useState<number>(
    initialMonthlyBudgetEur ?? 20,
  );
  const [showKey, setShowKey] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(mode === 'create');
  const [clientFieldErrors, setClientFieldErrors] = useState<{
    apiKey?: string;
    modelDefault?: string;
    monthlyBudgetEur?: string;
  }>({});

  const mutation = useUpdateAnthropicAppConfig();

  const serverValidation =
    mutation.error instanceof AnthropicAppConfigValidationError
      ? mutation.error
      : null;
  const otherError =
    mutation.error && !serverValidation ? mutation.error : null;

  function fieldError(
    field: 'apiKey' | 'modelDefault' | 'monthlyBudgetEur',
  ): string | undefined {
    if (clientFieldErrors[field]) return clientFieldErrors[field];
    return serverValidation?.fieldErrors[field]?.[0];
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setClientFieldErrors({});

    // En modo claude_code mandamos apiKey:'' explícito + monthlyBudget:0
    // (no aplica budget); en api_key, comportamiento original.
    const payload =
      authMode === 'claude_code'
        ? {
            authMode: 'claude_code' as const,
            apiKey: '',
            modelDefault,
            monthlyBudgetEur: 0,
          }
        : {
            authMode: 'api_key' as const,
            apiKey,
            modelDefault,
            monthlyBudgetEur,
          };

    const parsed = AnthropicAppConfigPutBody.safeParse(payload);
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      setClientFieldErrors({
        apiKey: flat.fieldErrors.apiKey?.[0],
        modelDefault: flat.fieldErrors.modelDefault?.[0],
        monthlyBudgetEur: flat.fieldErrors.monthlyBudgetEur?.[0],
      });
      return;
    }

    mutation.mutate(parsed.data, {
      onSuccess: (response) => {
        // Limpiamos apiKey del input — no la queremos en memoria del DOM.
        setApiKey('');
        setShowKey(false);
        if (onSaved) onSaved(response);
      },
    });
  }

  return (
    <section
      aria-labelledby={`${apiKeyId}-title`}
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id={`${apiKeyId}-title`}
        className="text-lg font-semibold text-slate-900"
      >
        {mode === 'edit'
          ? 'Editar configuración de Anthropic'
          : 'Configura tu API key de Anthropic'}
      </h2>

      <p className="mt-2 text-sm text-slate-700">
        tri-mentor necesita tu propia API key de Anthropic para que el coach
        pueda generar planes con Claude. La key se guarda cifrada y nunca se
        envía al frontend después.
      </p>

      {mode === 'edit' && (
        <p
          role="note"
          className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          Cambiar la API key resetea el contador de tokens consumidos este
          mes. Si sólo quieres ajustar modelo o presupuesto, igualmente
          tendrás que reintroducir la key.
        </p>
      )}

      {/* Instrucciones colapsables */}
      <div className="mt-4">
        <button
          type="button"
          aria-expanded={instructionsOpen}
          aria-controls={instructionsId}
          onClick={() => setInstructionsOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 rounded"
        >
          <span aria-hidden="true">{instructionsOpen ? '▾' : '▸'}</span>
          {instructionsOpen ? 'Ocultar instrucciones' : 'Cómo conseguir tu API key'}
        </button>

        {instructionsOpen && (
          <ol
            id={instructionsId}
            className="mt-3 list-decimal space-y-2 pl-6 text-sm text-slate-700"
          >
            <li>
              Abre{' '}
              <a
                href="https://console.anthropic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 underline hover:text-teal-800"
              >
                console.anthropic.com
              </a>{' '}
              e inicia sesión (o crea una cuenta si aún no la tienes).
            </li>
            <li>
              Ve a <strong>Settings → API Keys</strong> y pulsa{' '}
              <strong>Create Key</strong>. Dale un nombre descriptivo (p.ej.{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                tri-mentor local
              </code>
              ).
            </li>
            <li>
              Asegúrate de tener crédito o método de pago activo en tu cuenta
              de Anthropic. El coste depende del modelo y del tamaño de los
              prompts; el presupuesto mensual de abajo es un freno suave para
              evitar sustos.
            </li>
            <li>
              Copia la key (empieza por{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                sk-ant-…
              </code>
              ) y pégala abajo. Es la única vez que Anthropic te la mostrará
              completa.
            </li>
          </ol>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
        {/* Modo de autenticación (M2.9) */}
        <fieldset className="rounded-md border border-slate-200 p-3">
          <legend className="px-1 text-sm font-medium text-slate-800">
            Modo de autenticación
          </legend>
          <div className="mt-2 space-y-2">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
              <input
                type="radio"
                name={authModeId}
                value="api_key"
                checked={authMode === 'api_key'}
                onChange={() => setAuthMode('api_key')}
                className="mt-1"
              />
              <span>
                <strong>API key (Anthropic Console)</strong>
                <span className="block text-xs text-slate-500">
                  Recomendado. Usas tu propia API key y tu propio presupuesto
                  facturado por Anthropic.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
              <input
                type="radio"
                name={authModeId}
                value="claude_code"
                checked={authMode === 'claude_code'}
                onChange={() => setAuthMode('claude_code')}
                className="mt-1"
              />
              <span>
                <strong>Claude Max (claude-code CLI)</strong>
                <span className="block text-xs text-slate-500">
                  Usa tu sesión de Claude Max ya autenticada en el host. Sin
                  API key, sin presupuesto.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {authMode === 'claude_code' && (
          <p
            role="note"
            className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            Requiere el CLI <code className="rounded bg-amber-100 px-1">
              claude
            </code>{' '}
            instalado en el servidor y autenticado con tu cuenta de Claude
            Max. No habrá medición de tokens ni presupuesto mensual.
          </p>
        )}

        {/* API Key (sólo en modo api_key) */}
        {authMode === 'api_key' && (
        <div>
          <label
            htmlFor={apiKeyId}
            className="block text-sm font-medium text-slate-800"
          >
            API Key
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              id={apiKeyId}
              name="apiKey"
              type={showKey ? 'text' : 'password'}
              autoComplete="off"
              spellCheck={false}
              required
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value.trim())}
              aria-invalid={fieldError('apiKey') ? true : undefined}
              aria-describedby={
                fieldError('apiKey') ? `${apiKeyId}-err` : undefined
              }
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
              placeholder="sk-ant-..."
            />
            <button
              type="button"
              aria-pressed={showKey}
              aria-label={showKey ? 'Ocultar API key' : 'Mostrar API key'}
              onClick={() => setShowKey((v) => !v)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
            >
              {showKey ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          {fieldError('apiKey') && (
            <p
              id={`${apiKeyId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('apiKey')}
            </p>
          )}
        </div>
        )}

        {/* Modelo por defecto */}
        <div>
          <label
            htmlFor={modelId}
            className="block text-sm font-medium text-slate-800"
          >
            Modelo por defecto
          </label>
          <select
            id={modelId}
            name="modelDefault"
            value={modelDefault}
            onChange={(e) => setModelDefault(e.target.value as AnthropicModel)}
            aria-invalid={fieldError('modelDefault') ? true : undefined}
            aria-describedby={
              fieldError('modelDefault') ? `${modelId}-err` : undefined
            }
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            {ANTHROPIC_MODELS.map((m) => (
              <option key={m} value={m}>
                {modelLabel(m)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            Sonnet es el equilibrio recomendado. Haiku abarata pero pierde
            calidad de razonamiento; Opus es caro y sólo merece la pena para
            planes muy complejos.
          </p>
          {fieldError('modelDefault') && (
            <p
              id={`${modelId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('modelDefault')}
            </p>
          )}
        </div>

        {/* Presupuesto mensual (sólo en modo api_key) */}
        {authMode === 'api_key' && (
        <div>
          <label
            htmlFor={budgetId}
            className="block text-sm font-medium text-slate-800"
          >
            Presupuesto mensual (EUR)
          </label>
          <input
            id={budgetId}
            name="monthlyBudgetEur"
            type="number"
            min={0}
            max={1000}
            step={1}
            required
            value={monthlyBudgetEur}
            onChange={(e) => {
              const raw = e.target.value;
              // Si el usuario borra todo, mantenemos 0 para que el schema
              // no truene con NaN antes de teclear el siguiente dígito.
              const n = raw === '' ? 0 : Number.parseInt(raw, 10);
              setMonthlyBudgetEur(Number.isFinite(n) ? n : 0);
            }}
            aria-invalid={fieldError('monthlyBudgetEur') ? true : undefined}
            aria-describedby={
              fieldError('monthlyBudgetEur')
                ? `${budgetId}-err`
                : `${budgetId}-help`
            }
            className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
          />
          <p id={`${budgetId}-help`} className="mt-1 text-xs text-slate-500">
            Tope blando: cuando el consumo estimado del mes alcance este
            importe, el coach dejará de generar planes hasta el mes
            siguiente. Estimación conservadora: ~200.000 tokens por EUR.
          </p>
          {fieldError('monthlyBudgetEur') && (
            <p
              id={`${budgetId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('monthlyBudgetEur')}
            </p>
          )}
        </div>
        )}

        {/* Errores generales (formErrors o errores no de validación) */}
        {(serverValidation?.formErrors.length || otherError) && (
          <div
            id={formErrorsId}
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            {serverValidation?.formErrors.length
              ? serverValidation.formErrors.join(' ')
              : otherError?.message ??
                'No se pudo guardar la configuración de Anthropic.'}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={mutation.isPending}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
